import React from "react"
import { useDispatch } from "react-redux"
import { DivBtn } from "web/components/generic/DivBtn"
import { GameIcon } from 'web/components/generic/GameIcon'
import { RosterPlayer } from "web/data/models"
import { ModalActions } from "web/redux/slices/modal-slice"
import { RosterActions, useRosterState } from "web/redux/slices/roster-slice"
import { getCurrentBuild } from 'web/data/core-data'
import { SvgCross } from 'web/components/generic/SvgCross'
import { WowheadLink } from 'web/components/generic/WowheadLink'

export const PlayerList: React.FC<{ players: RosterPlayer[] }> = (props) => {

    const disp = useDispatch()
    const buildData = getCurrentBuild()

    const rosterState = useRosterState()
    const rosterPlayerNames = rosterState.players.map(player => player.name)

    const players = props.players.map(player => {
        const spec = buildData.Spec(player.specId)

        const iconFn = (isSelected: boolean, game_id?: number, icon_name?: string, link_suffix?: string) => (
            <WowheadLink gameData={{ game_id, icon_name, link_suffix }}>
                <div style={{
                    opacity: (isSelected) ? 1 : 0.3,
                    border: "1px solid rgb(61, 61, 61)",
                }}>
                    <GameIcon
                        size="22px"
                        icon_name={icon_name}
                    />
                </div>
            </WowheadLink>
        )

        const spells = spec.spellIds.reduce<JSX.Element[]>((acc, curr) => {
            const spell = buildData.Spell(curr)

            // We only want to show baseline spells
            if (spell.talentId != null || spell.modifierId != null) {
                return acc
            }

            // If a spell is replaced by a talent, and the talent is
            // chose, dim out the base spell
            const isSelected = (() => {
                if (spell.replacedByTalentId == null) {
                    return true
                }

                const talent = buildData.Talent(spell.replacedByTalentId)
                return player.talentIds.includes(talent.id) === false
            })()

            return [
                ...acc,
                iconFn(isSelected, spell.game_id, spell.icon_name, spell.link_suffix)
            ]
        }, [])

        const talentsAndMods = spec.talentIds.map(talentId => {
            const talent = buildData.Talent(talentId)
            const isSelected = player.talentIds.includes(talent.id)

            return iconFn(isSelected, talent.game_id, talent.icon_name, talent.link_suffix)
        })

        for (const key in buildData.modifiersById) {
            const modifier = buildData.Modifier(key)
            const isSelected = player.modifierIds.includes(modifier.id)

            if (modifier.eligibleSpecIds.includes(spec.id)) {
                talentsAndMods.push(iconFn(isSelected, modifier.game_id, modifier.icon_name, modifier.link_suffix))
            }
        }

        const removePlayer = () => disp(RosterActions.removePlayer({ playerId: player.id }))

        return (
            <div key={player.id} className="d-flex flex-column border">
                <div className="d-flex justify-content-between flex-grow-1">
                    <DivBtn fillWidth onClick={() => {
                        disp(ModalActions.openEditPlayerModal({
                            mode: "roster-edit",
                            title: "Edit player",
                            saveBtnText: "Save changes",
                            player: player,
                            rosterPlayerNames,
                            onConfirm: (player) => {
                                disp(RosterActions.updatePlayer({ player }))
                            },
                        }))
                    }}>
                        <div className="d-flex align-items-center space-mx-2">
                            <GameIcon
                                size="48px"
                                icon_name={spec.icon_name}
                                style={{ opacity: 0.3 }}
                            />

                            <div className="ml-2">{player.name}</div>
                        </div>
                    </DivBtn>

                    <DivBtn onClick={removePlayer}>
                        <div className="border-left small d-flex py-3 align-items-center justify-content-center" style={{ width: 50 }}>
                            <SvgCross />
                        </div>
                    </DivBtn>
                </div>

                <div className="d-flex flex-column p-2 px-3 pb-3 bg-light border-top">
                    <div className="d-flex flex-column mb-3">
                        <div className="d-flex small font-weight-bold mb-2">
                            {"Spells"}
                        </div>

                        <div className="d-flex flex-wrap space-mx-2 flex-wrap row-gap-2">
                            {spells.length <= 0 ? (
                                <div className="small text-muted">
                                    {'None'}
                                </div>
                            ) : (
                                spells
                            )}
                        </div>
                    </div>

                    {talentsAndMods.length >= 1 &&
                        <div className="d-flex flex-column">
                            <div className="d-flex small font-weight-bold mb-2">
                                {"Talents & Modifiers"}
                            </div>

                            <div className="d-flex space-mx-2 flex-wrap row-gap-2">
                                {talentsAndMods}
                            </div>
                        </div>}
                </div>
            </div>
        )
    })

    if (players.length <= 0) {
        return (
            <div className="d-flex justify-content-center py-3 align-items-center text-muted small border">
                {"No players"}
            </div>
        )
    }

    return (
        <div className="d-flex flex-column space-my-3">
            {players}
        </div>
    )
}
